import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchCurrentUser } from '../../api/auth';
import { TemperatureUnitContext } from '../../util/useTemperatureUnit';
import { normalizeTemperatureUnit } from '../../util/temperature';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function normalizePermissions(permissions) {
  return Array.isArray(permissions) ? permissions : [];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const loadAuth = useCallback(async () => {
    setIsAuthLoading(true);
    setAuthError(null);

    try {
      const response = await fetchCurrentUser();
      setUser(response?.user || null);
      setPermissions(normalizePermissions(response?.permissions));
      setRoles(Array.isArray(response?.roles) ? response.roles : []);
    } catch (error) {
      setUser(null);
      setPermissions([]);
      setRoles([]);
      setAuthError(error);
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAuth();
  }, [loadAuth]);

  const patchUser = useCallback((partial) => {
    setUser((current) => (current ? { ...current, ...partial } : current));
  }, []);

  const hasPermission = useCallback(
    (permission) => {
      if (!permission || (Array.isArray(permission) && permission.length === 0)) {
        return true;
      }

      const required = Array.isArray(permission) ? permission : [permission];
      return required.some((value) => permissions.includes(value));
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions) => {
      if (!requiredPermissions?.length) {
        return true;
      }

      return requiredPermissions.every((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  const value = useMemo(
    () => ({
      user,
      permissions,
      roles,
      isAuthLoading,
      authError,
      isAuthenticated: Boolean(user),
      hasPermission,
      hasAllPermissions,
      reloadAuth: loadAuth,
      patchUser,
    }),
    [authError, hasAllPermissions, hasPermission, isAuthLoading, loadAuth, patchUser, permissions, roles, user]
  );

  return (
    <TemperatureUnitContext.Provider value={normalizeTemperatureUnit(user?.temperature_unit)}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </TemperatureUnitContext.Provider>
  );
}
